import { addYears, format, parse, subYears } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import { connect } from "react-redux";
import Select from "react-select";
import { Actions as EMPLOYEEACTIONS } from "../../../store/actions/employeeActions";
import { Actions as INTEGRATION_ACTIONS } from "../../../store/actions/integrationActions";
// import { TimeInputFormat, DateInputFormat } from '../../DateComponents';
import Tooltip from "@/tooltip";
import "react-datepicker/dist/react-datepicker.css";

import cleanDeep from "clean-deep";
import { merge, setWith } from "lodash";
import { NotificationManager } from "react-notifications";
import AddressLookUp from "../../common/AddressLookUp";
import { DateInputFormat, handleRawDate } from "../../common/DateComponents";
import LoadingContent from "../../common/LoadingContent";
import IntegrationEmployeeLink from "../../integrations/IntegrationEmployeeLink";

const countryCodes = require("country-codes-list");
const myCountryObject = countryCodes.customList("countryCode", "{countryNameEn}");

const myFullCountryList = [];

//This allows us to treat the object from the library the way we need to use React Select.
//Object entries allows us to deal with key and value, and treat them the way we want.
Object.entries(myCountryObject).forEach(([key, value]) => {
	let obj = {};
	obj.value = key;
	obj.label = value;
	myFullCountryList.push(obj);
});

const maritalStatusOptions = [
	{ label: "Single", value: "Single" },
	{ label: "Married", value: "Married" },
	{ label: "Divorced", value: "Divorced" },
	{ label: "Widow", value: "Widow" },
	{ label: "Single Parent", value: "Single Parent" },
];

const genderOptions = [
	{ label: "Male", value: "Male" },
	{ label: "Female", value: "Female" },
	{ label: "Non-Binary", value: "Non-Binary" },
];

const legalGenderOptions = [
	{ label: "Male", value: "Male" },
	{ label: "Female", value: "Female" },
];

const holidayPreferenceOptions = [
	{ label: "England and Wales", value: undefined },
	{ label: "Scotland", value: "scotland" },
	{ label: "Northern Ireland", value: "northern-ireland" },
];

const EmployeeDetails = ({
	jurisdiction,
	saveEmployeeDetails,
	agreementInfo,
	disabled,
	forNewEmployee,
	type,
	prevPage,
	nextPage,
	referenceLink,
	notification,
	linkEmployee,
	engagementDate,
	forReview,
}) => {
	const [data, setData] = useState({});
	const updateField = (field, value) => {
		// console.debug(field, value)
		if (!field) return;
		if (field.includes("[")) field = setWith({}, field, value, Object);

		const newData = merge(data, { ...(typeof field == "object" ? field : { [field]: value }) });

		setData({ ...newData });
	};

	const { employeeDetails: employee, id: agreement, reference } = { ...agreementInfo };

	const agreementID = useMemo(() => {
		return String(agreement);
	}, [agreement]);

	const spouseInitials = useMemo(() => {
		const initials = [data.spouse?.givenName, data.spouse?.familyName].reduce((prev, curr) => {
			prev =
				prev +
				(curr
					?.split(" ")
					?.map((c) => c[0]?.toUpperCase())
					?.join("") || "");
			return prev;
		}, "");
		return initials;
	}, [data.spouse?.familyName, data.spouse?.givenName, agreementID]);

	useEffect(() => {
		const hasDetails = employee && Object.keys(cleanDeep(employee))?.length > 0;
		if (!hasDetails) return;
		setData({
			// * Company References
			companyPhone: employee?.companyPhone,
			companyEmail: employee?.companyEmail,
			reference: reference ? reference : employee?.reference,
			// * Personal Details
			title: employee?.title,
			givenName: employee?.givenName,
			familyName: employee?.familyName,
			gender: employee?.gender,
			legalGender: employee?.legalGender,
			holidayPreference: employee?.holidayPreference,
			dateOfBirth: employee?.dateOfBirth,
			phone: employee?.phone,
			email: employee?.email,
			hasChildren: employee?.hasChildren,
			deferredUntil: employee?.deferredUntil,
			// * Marital Details
			maritalStatus: employee?.maritalStatus,
			spouse: employee?.spouse
				? {
						familyName: employee?.spouse?.familyName,
						givenName: employee?.spouse?.givenName,
						marriageDate: employee?.spouse?.marriageDate,
						idNo: employee?.spouse?.idNo,
						responsible: employee?.spouse?.responsible,
					}
				: undefined,
			// * Address
			address: {
				houseNumber: employee?.address?.houseNumber,
				postCode: employee?.address?.postCode,
				city: employee?.address?.city,
				street: employee?.address?.street,
				county: employee?.address?.county,
				subBuilding: employee?.address?.subBuilding,
				locality: employee?.address?.locality,
				country: employee?.address?.country,
			},
			// * Identification
			identity: (Array.isArray(employee?.identificationNumbers) ? employee?.identificationNumbers : [])?.reduce(
				(prev, curr) => {
					prev[curr.type] = curr.no;
					return prev;
				},
				{},
			),
			payrollId: employee?.payrollId,
			nationality: employee?.nationality,
		});
	}, [agreementID]);

	const onSubmit = (e) => {
		e.preventDefault();
		e.stopPropagation();
		const newData = { ...data };
		console.debug(newData);
		switch (jurisdiction) {
			case "UK":
				if (!newData.legalGender && !["cis"].includes(type)) {
					NotificationManager.error("Legal gender is required!", "Saving Employee Details");
					return;
				}
			// ! Disabled As Requested on Issue #861mqmzvb - ClickUp
			// if (!newData.maritalStatus) {
			//   NotificationManager.error('Marital Status is required!', 'Saving Employee Details')
			//   return
			// }
		}
		if (newData.maritalStatus === "Married" && spouseInitials) newData.spouse.initials = spouseInitials;

		if (newData.identity) {
			newData.identificationNumbers = [];
			Object.entries({ ...newData.identity }).map((index) => {
				const [type, no] = index;
				if (no != "") {
					newData.identificationNumbers.push({
						type,
						no,
						countryOfIssue: jurisdiction,
					});
				}
			});
			delete newData.identity;
		}

		saveEmployeeDetails(
			cleanDeep(newData, { emptyStrings: false, emptyArrays: false, emptyObjects: false }),
			agreementInfo.id,
			notification,
			nextPage
		);
		console.debug(newData);
		linkEmployee();
	};

	if (!employee && !forNewEmployee && !forReview) return <LoadingContent />;

	return (
		<form onSubmit={onSubmit} className="px-8">
			<div className="col-xs-12 form-section">
				<div className="col-xs-12 section-header">
					<h1>Company References</h1>
				</div>

				<div className="col-xs-3">
					<div className="form-material form-container">
						<label htmlFor="companyPhone">Company Phone Number</label>
						<input
							className="form-control form-input"
							id="companyPhone"
							name="companyPhone"
							value={data?.companyPhone}
							onChange={(e) => {
								updateField(e.target.name, e.target.value);
							}}
							autoComplete="off"
							disabled={disabled}
						/>
					</div>
				</div>

				<div className="col-xs-3 ">
					<div className="form-material form-container">
						<label htmlFor="companyEmail">
							<Tooltip content="The company email address is used to send leave approval requests from other team members, and is otherwise for informational purposes.">
								Company Email Address
							</Tooltip>
						</label>
						<input
							id="companyEmail"
							type="email"
							name="companyEmail"
							value={data?.companyEmail}
							data-rule-email="true"
							autoComplete="off"
							className="form-control form-input"
							onChange={(e) => {
								updateField(e.target.name, e.target.value);
							}}
							disabled={disabled}
						/>
					</div>
				</div>

				{/* Deputy Link / Reference Code*/}
				<IntegrationEmployeeLink
					employeeReference={reference}
					agreementInfo={agreementInfo}
					value={data?.reference}
					onChange={(name, value) => updateField(name, value)}
					disabled={disabled}
				/>
				<div className="clearfix"></div>
			</div>

			<div className="col-xs-12 form-section">
				<div className="section-header">
					<h1>Personal Details</h1>
				</div>

				<div className="section-body">
					{jurisdiction == "UK" && (
						<div className="col-xs-3">
							<div className="form-material form-container">
								<label htmlFor="title">Title</label>
								<input
									className="form-control form-input"
									type="text"
									id="title"
									name="title"
									value={data?.title}
									onChange={(e) => {
										updateField(e.target.name, e.target.value);
									}}
									autoComplete="off"
									disabled={disabled}
								/>
							</div>
						</div>
					)}

					<div className="col-xs-3">
						<div className="form-material form-container">
							<label htmlFor="givenName">First Name</label>
							<input
								className="form-control form-input"
								id="givenName"
								name="givenName"
								value={data?.givenName}
								required="required"
								onChange={(e) => {
									updateField(e.target.name, e.target.value);
								}}
								autoComplete="off"
								disabled={disabled}
							/>
						</div>
					</div>

					<div className="col-xs-3 ">
						<div className="form-material form-container">
							<label htmlFor="familyName">Last Name</label>
							<input
								className="form-control form-input"
								id="familyName"
								name="familyName"
								value={data?.familyName}
								required="required"
								onChange={(e) => {
									updateField(e.target.name, e.target.value);
								}}
								autoComplete="off"
								disabled={disabled}
							/>
						</div>
					</div>

					<div className="col-xs-3 ">
						<div className="form-material form-container">
							<label htmlFor="gender">
								<Tooltip content={"Gender as personally identified"}>Gender</Tooltip>
							</label>
							<Select
								isDisabled={disabled}
								name="gender"
								options={genderOptions}
								value={genderOptions?.find(({ value }) => value == data?.gender)}
								autoComplete="off"
								onChange={(e) => {
									updateField("gender", e?.value || null);
								}}
							/>
						</div>
					</div>

					<div className="col-xs-3">
						<div className="form-material form-container">
							<DatePicker
								autoComplete="off"
								dateFormat="dd MMM yyyy"
								minDate={subYears(new Date(), 100)}
								maxDate={subYears(new Date(), 14)}
								wrapperClassName="full-width"
								required={true}
								className="form-control form-input"
								customInput={<DateInputFormat />}
								onChange={(date) => {
									updateField("dateOfBirth", date ? format(date, "yyyy-MM-dd") : undefined);
								}}
								onChangeRaw={(e) => {
									handleRawDate(e.target.value, (date) => {
										updateField("dateOfBirth", date ? format(date, "yyyy-MM-dd") : undefined);
									});
								}}
								strictParsing
								selected={data.dateOfBirth && parse(data.dateOfBirth, "yyyy-MM-dd", new Date())}
							/>
							<label htmlFor="dateOfBirth">Date of Birth</label>
						</div>
					</div>

					<div className="col-xs-3 ">
						<div className="form-material form-container">
							<label htmlFor="phone">Phone</label>
							<input
								id="phone"
								className="form-control form-input"
								name="phone"
								autoComplete="off"
								value={data?.phone}
								onChange={(e) => {
									updateField(e.target.name, e.target.value);
								}}
								disabled={disabled}
							/>
						</div>
					</div>

					<div className="col-xs-3 ">
						<div className="form-material form-container">
							<label htmlFor="email">
								<Tooltip content="The personal email address is used for employee login and sending payslips. Leave Blank if you don't have it">
									Personal Email Address
								</Tooltip>
							</label>
							<input
								id="email"
								className="form-control form-input"
								type="email"
								name="email"
								disabled={disabled}
								autoComplete="off"
								value={data?.email}
								onChange={(e) => {
									updateField(e.target.name, e.target.value);
								}}
								data-rule-email="true"
							/>
						</div>
					</div>
					{jurisdiction == "UK" && (
						<>
							<div className="col-xs-3">
								<div className="form-material form-container">
									<label htmlFor="material-text">
										<Tooltip content={"Gender as legally stated on passport or identification"}>
											Legal Gender
										</Tooltip>
									</label>
									<Select
										isDisabled={disabled}
										name="legalGender"
										options={legalGenderOptions}
										value={legalGenderOptions?.find((item) => item.value == data?.legalGender)}
										autoComplete="off"
										onChange={(e) => {
											updateField("legalGender", e?.value || null);
										}}
									/>
								</div>
							</div>
							<div className="clearfix" />
							<div className="col-xs-3">
								<div className="form-material form-container">
									<label htmlFor="material-text">
										<Tooltip
											content={
												"The Bank Holidays as preferred by their residing country or payroll issuer"
											}
										>
											Bank Holiday Preference
										</Tooltip>
									</label>
									<Select
										isDisabled={disabled}
										name="holidayPreference"
										options={holidayPreferenceOptions}
										value={holidayPreferenceOptions?.find(
											(item) => item.value == data?.holidayPreference,
										)}
										autoComplete="off"
										onChange={(e) => {
											updateField("holidayPreference", e?.value || null);
										}}
									/>
								</div>
							</div>
						</>
					)}
					<div className="col-xs-3">
						<div className="form-container">
							<label className="css-input switch switch-sm switch-success">
								<input
									id="hasChildren"
									name="hasChildren"
									className="checkbox-toggle"
									checked={data?.hasChildren}
									onClick={(e) => {
										updateField(e.target.name, !data?.hasChildren);
									}}
									onChange={(e) => {}}
									type="checkbox"
									disabled={disabled}
									autoComplete="off"
								/>
								<span />
								<label style={{ marginBottom: 0 }}>Has Children</label>
							</label>
						</div>
					</div>
					<div className="clearfix"></div>
				</div>
			</div>

			<div className="col-xs-12 form-section">
				<div className="section-header">
					<h1>Marital Details</h1>
				</div>
				<div className="col-xs-12 pl-0">
					<div className="col-xs-3">
						<div className="form-material form-container">
							<label htmlFor="material-text">Marital Status</label>
							<Select
								isDisabled={disabled}
								isClearable={true}
								name="maritalStatus"
								options={maritalStatusOptions}
								value={maritalStatusOptions?.find((item) => item.value == data?.maritalStatus)}
								onChange={(e) => {
									updateField("maritalStatus", e?.value || null);
								}}
							/>
						</div>
					</div>

					{data?.maritalStatus == "Married" && (
						<>
							<div className="col-xs-3">
								<div className="form-material form-container">
									<input
										className="form-control form-input"
										id="spouse[idNo]"
										name="spouse[idNo]"
										value={data?.spouse?.idNo}
										onChange={(e) => {
											updateField(e.target.name, e.target.value);
										}}
										disabled={disabled}
										autoComplete="off"
									/>
									<label htmlFor="spouse[idNo]">
										{jurisdiction == "UK" ? "Spouse NI Number" : "Spouse I.D. Card #"}
									</label>
								</div>
							</div>

							<div className="col-xs-3">
								<div className="form-material form-container">
									<DatePicker
										autoComplete="off"
										dateFormat="dd MMM yyyy"
										minDate={subYears(new Date(), 100)}
										maxDate={addYears(new Date(), 100)}
										wrapperClassName="full-width"
										customInput={<DateInputFormat />}
										className="form-control form-input"
										onChange={(date) => {
											updateField(
												"spouse[marriageDate]",
												date ? format(date, "yyyy-MM-dd") : undefined,
											);
										}}
										onChangeRaw={(e) => {
											handleRawDate(e.target.value, (date) => {
												updateField(
													"spouse[marriageDate]",
													date ? format(date, "yyyy-MM-dd") : undefined,
												);
											});
										}}
										strictParsing
										selected={
											data.spouse?.marriageDate &&
											parse(data.spouse?.marriageDate, "yyyy-MM-dd", new Date())
										}
									/>
									<label htmlFor="spouse[marriageDate]">Date of Marriage</label>
								</div>
							</div>

							{jurisdiction == "MT" && (
								<div className="col-xs-3">
									<div className="form-container">
										<label className="css-input switch switch-sm switch-success">
											<input
												id="spouse[responsible]"
												name="spouse[responsible]"
												className="checkbox-toggle"
												checked={data?.spouse?.responsible}
												onChange={(e) => {
													updateField(e.target.name, !data?.spouse.responsible);
												}}
												type="checkbox"
												disabled={disabled}
												autoComplete="off"
											/>
											<span /> Responsible Spouse
										</label>
									</div>
								</div>
							)}

							<div className="col-xs-12 pl-0">
								<div className="col-xs-4 col-xs-4">
									<div className="form-material form-container">
										<input
											className="form-control form-input"
											id="spouse[givenName]"
											name="spouse[givenName]"
											value={data?.spouse?.givenName}
											autoComplete="off"
											onChange={(e) => {
												updateField(e.target.name, e.target.value);
											}}
											disabled={disabled}
										/>
										<label htmlFor="spouse[givenName]">Spouse First Name</label>
										{spouseInitials ? (
											<>
												<span className="help-block">Initials: {spouseInitials}</span>
											</>
										) : null}
									</div>
								</div>

								<div className="col-xs-4 col-xs-4">
									<div className="form-material form-container">
										<input
											className="form-control form-input"
											id="spouse[familyName]"
											name="spouse[familyName]"
											value={data?.spouse?.familyName}
											autoComplete="off"
											onChange={(e) => {
												updateField(e.target.name, e.target.value);
											}}
											disabled={disabled}
										/>
										<label htmlFor="spouse[familyName]">Spouse Last Name</label>
									</div>
								</div>
							</div>
						</>
					)}
				</div>
				<div className="clearfix"></div>
			</div>

			<div className="col-lg-12 col-xs-12 px-0">
				<div className="col-lg-6 col-xs-6 form-section">
					<div
						className="section-header mb-0 px-0"
					>
						<h1>Address</h1>
					</div>
					<div className="col-xs-12">
						<AddressLookUp
							disabled={disabled}
							updateInput={(selectedOption) => {
								updateField(`address`, selectedOption);
							}}
						/>
					</div>

					{["UK"].includes(jurisdiction) ? (
						<>
							<div className="col-xs-6">
								<div className="form-material form-container">
									<input
										className="form-control form-input"
										id="address[subBuilding]"
										name="address[subBuilding]"
										value={data?.address?.subBuilding}
										onChange={(e) => {
											updateField(e.target.name, e.target.value);
										}}
										autoComplete="off"
										disabled={disabled}
									/>
									<label htmlFor="address[subBuilding]">Sub Building</label>
								</div>
							</div>
						</>
					) : null}

					<div className="col-xs-6">
						<div className="form-material form-container">
							<input
								className="form-control form-input"
								id="address[houseNumber]"
								name="address[houseNumber]"
								value={data?.address?.houseNumber}
								onChange={(e) => {
									updateField(e.target.name, e.target.value);
								}}
								autoComplete="off"
								disabled={disabled}
							/>
							<label htmlFor="address[houseNumber]">
								{["UK"].includes(jurisdiction) ? "Building" : "House"}
							</label>
						</div>
					</div>

					<div className={`col-xs-${["UK"].includes(jurisdiction) ? "12" : "6"}`}>
						<div className="form-material form-container">
							<input
								className="form-control form-input"
								id="address[street]"
								name="address[street]"
								value={data?.address?.street}
								onChange={(e) => {
									updateField(e.target.name, e.target.value);
								}}
								autoComplete="off"
								disabled={disabled}
							/>
							<label htmlFor="address[street]">Street</label>
						</div>
					</div>

					<div className="col-xs-6">
						<div className="form-material form-container">
							<input
								className="form-control form-input"
								id="address[city]"
								name="address[city]"
								value={data?.address?.city}
								onChange={(e) => {
									updateField(e.target.name, e.target.value);
								}}
								autoComplete="off"
								disabled={disabled}
							/>
							<label htmlFor="address[city]">City</label>
						</div>
					</div>

					{["UK"].includes(jurisdiction) ? (
						<>
							<div className="col-xs-6">
								<div className="form-material form-container">
									<input
										className="form-control form-input"
										id="address[locality]"
										name="address[locality]"
										value={data?.address?.locality}
										onChange={(e) => {
											updateField(e.target.name, e.target.value);
										}}
										autoComplete="off"
										disabled={disabled}
									/>
									<label htmlFor="address[locality]">Locality</label>
								</div>
							</div>
							<div className="col-xs-4">
								<div className="form-material form-container">
									<input
										className="form-control form-input"
										id="address[county]"
										name="address[county]"
										value={data?.address?.county}
										onChange={(e) => {
											updateField(e.target.name, e.target.value);
										}}
										autoComplete="off"
										disabled={disabled}
									/>
									<label htmlFor="address[county]">County</label>
								</div>
							</div>
							<div className="col-xs-4">
								<div className="form-material form-container">
									<input
										className="form-control form-input"
										id="address[country]"
										name="address[country]"
										value={data?.address?.country}
										onChange={(e) => {
											updateField(e.target.name, e.target.value);
										}}
										autoComplete="off"
										disabled={disabled}
									/>
									<label htmlFor="address[country]">Country</label>
								</div>
							</div>
						</>
					) : null}

					<div className={`col-xs-${["UK"].includes(jurisdiction) ? 4 : 6}`}>
						<div className="form-material form-container">
							<input
								className="form-control form-input"
								id="address[postCode]"
								name="address[postCode]"
								value={data?.address?.postCode}
								autoComplete="off"
								onChange={(e) => {
									updateField(e.target.name, e.target.value);
								}}
								disabled={disabled}
							/>
							<label htmlFor="address[postCode]">Postcode</label>
						</div>
						<div className="clearfix" />
					</div>
					<div className="clearfix" />
				</div>

				<div className="col-lg-6 col-xs-6 form-section">
					<div className="section-header">
						<h1>Identification</h1>
					</div>

					{["MT"].includes(jurisdiction) ? (
						<>
							<div className="col-xs-6">
								<div className="form-material form-container">
									<label htmlFor="identity[NationalID]">I.D. Card #</label>
									<input
										className="form-control form-input"
										id="identity[NationalID]"
										name="identity[NationalID]"
										value={data?.identity?.["NationalID"]}
										required="required"
										autoComplete="off"
										onChange={(e) => {
											updateField(e.target.name, e.target.value);
										}}
										disabled={disabled}
									/>
									{/* <div
                id="identity-card-exists"
                className="help-block animated fadeInDown hide"
              >
                We recognise this identity. This person will be asked to
                share their details once you complete the agreement.
              </div>
              <div
                id="agreement-exists"
                className="help-block animated fadeInDown hide"
              >
                We recognise this identity. This person already has an
                agreement with your company.
                <a href="" target="_blank">
                  To edit this agreement click here.
                </a>
              </div> */}
								</div>
							</div>
						</>
					) : null}

					<div className="col-xs-6">
						<div className="form-material form-container">
							<input
								className="form-control form-input"
								id="identity[SocialSecurity]"
								name="identity[SocialSecurity]"
								value={data?.identity?.["SocialSecurity"]}
								onChange={(e) => {
									const value = e.target.value.toUpperCase();
									updateField(e.target.name, value);
								}}
								disabled={disabled}
								autoComplete="off"
							/>
							<label htmlFor="identity[SocialSecurity]">
								{jurisdiction == "UK" ? "NI Number" : "Social Security Number"}
							</label>
						</div>
					</div>

					{jurisdiction == "MT" && (
						<div className="col-xs-6">
							<div className="form-material form-container">
								<input
									className="form-control form-input"
									id="identity[TaxNo]"
									name="identity[TaxNo]"
									value={data?.identity?.["TaxNo"]}
									onChange={(e) => {
										updateField(e.target.name, e.target.value);
									}}
									disabled={disabled}
									autoComplete="off"
								/>
								<label htmlFor="identity[TaxNo]">Tax Number</label>
							</div>
						</div>
					)}

					{jurisdiction == "UK" && (
						<div className="col-xs-6">
							<div className="form-material form-container">
								<input
									className="form-control form-input"
									id="payrollId"
									name="payrollId"
									value={data?.payrollId}
									onChange={(e) => {
										updateField(e.target.name, e.target.value);
									}}
									disabled={disabled}
									autoComplete="off"
								/>
								<label htmlFor="payrollId">Payroll ID</label>
							</div>
						</div>
					)}

					<div className="col-xs-6">
						<div className="form-material form-container">
							<label htmlFor="example2-select2">Nationality</label>
							<Select
								isDisabled={disabled}
								isClearable={true}
								id="nationality"
								name="nationality"
								options={myFullCountryList}
								value={myFullCountryList?.find((item) => item.value == data?.nationality)}
								onChange={(e) => {
									updateField("nationality", e?.value || null);
								}}
							/>
						</div>
					</div>
					<div className="clearfix"></div>
				</div>
			</div>

			{!disabled && (
				<div className="col-xs-12 pl-0 pr-0">
					{forNewEmployee ? (
						<>
							{prevPage && (
								<div
									className="btn btn-default pull-left"
									onClick={prevPage}
								>
									Back
								</div>
							)}
							<button
								style={{ marginLeft: "1rem" }}
								className="btn btn-default pull-right"
								onClick={nextPage}
							>
								Skip
							</button>
							<button
								style={{ marginLeft: "1rem" }}
								className="btn btn-success pull-right"
								type="submit"
							>
								Save &amp; Continue
							</button>
						</>
					) : (
						<button
							className="btn btn-success pull-right"
							type="submit"
						>
							Update Employee Details
						</button>
					)}
				</div>
			)}
			<div className="clearfix"></div>
		</form>
	);
};

const mapStateToProps = (state, ownProps) => {
	const { agreementInfo } = state.employee;
	const { forReview, forReviewAgreement, notification } = ownProps;

	return {
		jurisdiction: state?.company?.settings?.jurisdiction,
		agreementInfo: forReview ? { id: forReviewAgreement, employeeDetails: { ...forReview } } : agreementInfo,
		engagementDate: agreementInfo?.engagementDate
			? parse(agreementInfo?.engagementDate, "yyyy-MM-dd", new Date())
			: null,
		disabled: state?.permissions?.my?.permissions?.people == "Read",
		referenceLink: state.employee?.referenceLink,
	};
};

const mapDispatchToProps = (dispatch) => ({
	saveEmployeeDetails: (input, agreement, notification, onSuccess) =>
		dispatch(EMPLOYEEACTIONS.saveEmployeeDetails(input, null, { agreement, notification, onSuccess })),
	linkEmployee: () => dispatch(INTEGRATION_ACTIONS.linkEmployee()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EmployeeDetails);
